import { ElementRef, Injectable } from '@angular/core';
import { HttpClient, HttpParams } from '@angular/common/http';
import { environment } from 'src/environments/environment';
import { BehaviorSubject, Observable, catchError, map, of } from 'rxjs';
import { AbstractControl, AsyncValidatorFn, FormArray, FormGroup } from '@angular/forms';

@Injectable({
  providedIn: 'root'
})

export class CommonService {

  imageFormats = ['jpg', 'jpeg', 'png', 'gif', 'bmp', 'svg', 'webp', 'tiff'];
  apiUrl: string = '';
  private accountSubject = new BehaviorSubject<any>(null);
  public accountData$ = this.accountSubject.asObservable();

  constructor(
    private http: HttpClient
  ) { }

  getAccount() {
    return this.http.get<any>(`${environment.apiUrl}/api/account`)
      .pipe(map(res => {
        if (res && res.id) {
          localStorage.setItem('id', res.id);
        }

        // 2. Broadcast the data to the rest of the application
        this.accountSubject.next(res);

        return res;
      }));
  }

  GetDivision() {
    return this.http.get<any>(`${environment.apiUrl}/api/admin/divisions`)
      .pipe(map(res => {

        return res;
      }));
  }

  getLanguageType() {
    const storedLang = localStorage.getItem("lang");
    switch (storedLang) {
      case 'en':
        return 1;
      case 'fr':
        return 2;
      default:
        return 1;
    }
  }

  focusFirstInvalidControl(form: FormGroup | FormArray, element: ElementRef, index = 0) {
    if (form instanceof FormGroup) {
      // If it's a FormGroup, iterate over its controls
      for (const key of Object.keys(form.controls)) {
        const control = form.controls[key];
        if (control.invalid) {
          const invalidControl = element.nativeElement.querySelectorAll('[formcontrolname="' + key + '"]');
          if (invalidControl.length) {
            invalidControl[index].focus();
          } else {
            if (control instanceof FormGroup) {
              // If it's a nested FormGroup, recursively check its controls
              this.focusFirstInvalidControl(control, element);
            } else if (control instanceof FormArray) {
              // If it's a nested FormArray, handle its controls
              this.focusFirstInvalidControl(control, element);
            }
          }
          break;
        }
      }
    } else if (form instanceof FormArray) {
      // If it's a FormArray, iterate over its controls
      for (let i = 0; i < form.controls.length; i++) {
        const control = form.controls[i];
        if (control.invalid) {
          const invalidControl = element.nativeElement.querySelectorAll('[formcontrolname="' + i + '"]');
          if (invalidControl && invalidControl.length > 0) {
            invalidControl[0].focus();
          } else {
          }
          // If the control is a nested FormGroup, recursively check its controls
          if (control instanceof FormGroup) {
            this.focusFirstInvalidControl(control, element, i);
          } else if (control instanceof FormArray) {
            // If it's a nested FormArray, handle its controls
            this.focusFirstInvalidControl(control, element);
          }
          break;
        }
      }
    }
  }
  ResetPasswordInit(email: string, cliendId: string, options = {}) {
    return this.http.post<any>(`${environment.apiUrl}/api/account/reset-password/init?mail=${email}&clientId=${cliendId}`, null, {
      ...options,
      headers: {
        'X-Frontend-Origin': window.location.origin
      },
      observe: 'response'
    });
  }
  ResetPasswordFinish(payload: any) {
    return this.http.post<any>(`${environment.apiUrl}/api/account/reset-password/finish`, payload);
  }

  groupList(page: number, size: number, sortBy: any, sortDir: any, searchColumn: any, searchValue: any) {
    let params = new HttpParams()
      .set('page', page)
      .set('size', size)
      .set('sortBy', sortBy)
      .set('sortDir', sortDir);
    if (searchColumn && searchColumn.trim() !== '' && searchValue && searchValue.trim() !== '') {
      params = params.set('searchColumn', searchColumn).set('searchValue', searchValue);
    }
    return this.http.get<any>(`${environment.apiUrl}/api/empGroups`, { params });
  }


  getAllHoliday() {
    return this.http.get<any>(`${environment.apiUrl}/api/holiday-calendars/findHolidayIdAndName`)
      .pipe(map(res => {
        return res;
      }));
  }

  DepartmentList() {
    return this.http.get<any>(`${environment.apiUrl}/api/department/departmentNames`)
      .pipe(map(res => {
        return res;
      }));
  }

  SaveJobOpening(payload: any) {
    return this.http.post<any>(`${environment.apiUrl}/api/jobOpening/create`, payload)
      .pipe(map(res => {
        return res
      }))
  }

  JobOpeningList(page: number, size: number, sortDir: any, sortBy: any, searchColumn: any, searchValue: any, isInternal = false) {
    let params = new HttpParams()
      .set('page', page)
      .set('size', size)
      .set('sortBy', sortBy)
      .set('sortDir', sortDir);
    if (searchColumn && searchColumn.trim() !== '' && searchValue && searchValue.trim() !== '') {
      params = params.set('searchField', searchColumn).set('searchKeyword', searchValue);
    }
    if (isInternal) {
      params = params.set('isInternal', isInternal.toString());
    }
    return this.http.get<any>(`${environment.apiUrl}/api/jobOpening/all`, { params });
  }

  GetJobOpeningById(id: any) {
    return this.http.get<any>(`${environment.apiUrl}/api/jobOpening/${id}`)
      .pipe(map(res => {
        return res
      }))
  }

  SaveJobApplicant(payload: FormData) {
    return this.http.post<any>(`${environment.apiUrl}/api/jobApplicant/saveOrUpdateResume`, payload)
      .pipe(map(res => {
        return res;
      }));
  }
  GetJobApplicantById(id: any) {
    return this.http.get<any>(`${environment.apiUrl}/api/jobApplicant/getById/${id}`)
      .pipe(map(res => {
        return res;
      }));
  }


  GetJobApplicantList(page: number, size: number, sortDir: string, sortBy: string, searchField: string, searchKeyword: string) {
    return this.http.get<any>(`${environment.apiUrl}/api/jobApplicant/getAllData?pageNumber=${page}&pageSize=${size}&searchColumn=${searchField}&searchValue=${searchKeyword}&sortBy=${sortBy}&sortDir=${sortDir}`)
      .pipe(map(res => {
        return res
      }))
  }

  DownloadJobApplicantResume(url: any) {
    return this.http.get(`${url}`, { responseType: 'blob' })
      .pipe(map(res => {
        return res
      }))
  }

  CustomerList(page: number, size: number, sortDir: string, sortBy: string, searchField: string, searchKeyword: string) {
    return this.http.get<any>(`${environment.apiUrl}/api/customers/getAllData?page=${page}&size=${size}&searchColumn=${searchField}&searchValue=${searchKeyword}&sortBy=${sortBy}&sortDir=${sortDir}`)
      .pipe(map(res => {
        return res;
      }))
  }
  DepartmentNameList() {
    return this.http.get<any>(`${environment.apiUrl}/api/department/departmentNames`)
      .pipe(map(res => {
        return res;
      }))
  }

  getTermsAndConditionList(page: number, size: number, sortDir: string, sortBy: string, searchField: string, searchKeyword: string) {
    return this.http.get<any>(`${environment.apiUrl}/api/job-terms/term-condition?page=${page}&size=${size}&searchField=${searchField}&searchKeyword=${searchKeyword}&sortBy=${sortBy}&sortDir=${sortDir}`)
      .pipe(map(res => {
        return res;
      }));
  }

  addTerms(payload: any): Observable<any> {
    return this.http.post<any>(`${environment.apiUrl}/api/job-terms/create-terms-condition`, payload)
      .pipe(map(res => {
        return res;
      }));
  }

  ReimbursementTypeList() {
    return this.http.get<any>(`${environment.apiUrl}/api/reimbursement`)
      .pipe(map(res => {
        return res;
      }));
  }
  ReimbursementList(page: number, size: number, sortDir: string, sortBy: string, searchField: string, searchKeyword: string) {
    return this.http.get<any>(`${environment.apiUrl}/api/reimbursement/getPendingExpenseRequestList?&page=${page}&size=${size}&sortBy=${sortBy}&sortDir=${sortDir}&searchField=${searchField}&searchKeyword=${searchKeyword}`)
      .pipe(map(res => {
        return res;
      }))
  }
  ReimbursementListByProjectId(projectId: any, page: number, size: number, sortDir: string, sortBy: string, searchField: string, searchKeyword: string) {
    return this.http.get<any>(`${environment.apiUrl}/api/reimbursement/getPendingExpenseByProjectId?projectId=${projectId}&page=${page}&size=${size}&sortBy=${sortBy}&sortDir=${sortDir}&searchField=${searchField}&searchKeyword=${searchKeyword}`)
      .pipe(map(res => {
        return res;
      }))
  }
  SaveReimbursement(payload: FormData) {
    return this.http.post<any>(`${environment.apiUrl}/api/reimbursement/submitNewExpenseAction`, payload)
      .pipe(map(res => {
        return res;
      }));
  }

  GetReimbursementById(id: any) {
    return this.http.get<any>(`${environment.apiUrl}/api/reimbursement/getByRequestNumber?srNo=${id}`)
      .pipe(map(res => {
        return res;
      }));
  }
  SalaryElementGroupList(page: number, size: number, sortDir: string, sortBy: string, searchField: string, searchKeyword: string) {
    return this.http.get<any>(`${environment.apiUrl}/api/salaryElementGroup/findAll?page=${page}&size=${size}&searchField=${searchField}&searchKeyword=${searchKeyword}&sortBy=${sortBy}&sortDir=${sortDir}`)
      .pipe(map(res => {
        return res;
      }));
  }
  SaveSalaryElementGroup(payload: any) {
    return this.http.post<any>(`${environment.apiUrl}/api/salaryElementGroup/create`, payload)
      .pipe(map(res => {
        return res;
      }
      ));
  }
  GetSalaryElementGroupById(id: any) {
    return this.http.get<any>(`${environment.apiUrl}/api/salaryElementGroup/${id}`)
      .pipe(map(res => {
        return res;
      }));
  }


  GetJobApplicantDetails() {
    return this.http.get<any>(`${environment.apiUrl}/api/jobApplicant/details`)
      .pipe(map(res => {
        return res;
      }));
  }

  timesheetDashboard(startDate: string, endDate: string, username: number) {
    return this.http.get(`${environment.apiUrl}/api/timesheet/fetchWeeklyTimesheetByUserIdBetweenDates?startDate=${startDate}&endDate=${endDate}&timesheetUsername=${username}`)
      .pipe(map(res => {
        return res;
      }));
  }


  approveRejectExpense(payload: any): Observable<any[]> {
    return this.http.post<any[]>(`${environment.apiUrl}/api/reimbursement/bulk-approve`, payload)
      .pipe(map(res => {
        return res;
      }));
  }

  ReimbursementReportList(page: number, size: number, sortDir: string, sortBy: string, searchField: string, searchKeyword: string) {
    return this.http.get<any>(`${environment.apiUrl}/api/reimbursement/getRequests?&page=${page}&size=${size}&sortBy=${sortBy}&sortDir=${sortDir}&searchField=${searchField}&searchKeyword=${searchKeyword}`)
      .pipe(map(res => {
        return res;
      }))
  }


  downloadFile(fileUrl: string, filename?: string) {
    this.http.get(fileUrl, { responseType: 'blob' }).subscribe((blob) => {
      const a = document.createElement('a');
      const objectUrl = URL.createObjectURL(blob);
      a.href = objectUrl;
      a.download = filename ?? (fileUrl.split('/').pop() || 'download');
      a.click();
      URL.revokeObjectURL(objectUrl);
    });
  }

  getCustomerSiteDetails(projectId: number) {
    return this.http.get(`${environment.apiUrl}/api/customers/findCustomerDataByProjectId/${projectId}`)
      .pipe(map(res => {
        return res;
      }));
  }


  deleteSalaryElementById(groupId: number, elementId: number) {
    return this.http.delete(`${environment.apiUrl}/api/salaryElementGroup/deleteSalaryElementById/${groupId}/${elementId}`)
      .pipe(map(res => {
        return res;
      }));
  }

  getAllCustomersSites() {
    return this.http.get(`${environment.apiUrl}/api/customers/getAllCustomersSite`)
      .pipe(map(res => {
        return res;
      }));
  }

  getAllCountries() {
    return this.http.get(`${environment.apiUrl}/api/location/getAllCountries`)
      .pipe(map(res => {
        return res;
      }));
  }

  getTranslation(language: any): Observable<any> {
    if (this.isLocalEnvironment()) {
      return this.http.get(`/assets/json/EMSaaSTranslationData/json/${language}.json`);
    }
    const baseUrl = environment.apiUrl
    const translationUrl = `${baseUrl}/api/translation/get-by-lang?lang=${language}`;
    const headers = { 'X-Skip-Interceptor': 'true' };
    return this.http.get(translationUrl, { headers }).pipe(
      catchError(error => {
        console.warn('API failed, falling back to local assets', error);
        return this.http.get(`/assets/json/EMSaaSTranslationData/json/${language}.json`);
      })
    );
  }
  private isLocalEnvironment(): boolean {
    return environment.apiUrl.includes('localhost');
  }

  getCalendarById(calendarId: any, page: number, size: number, sortDir: string, sortBy: string, searchField: string, searchKeyword: string) {
    return this.http.get<any>(`${environment.apiUrl}/api/holiday-calendars/getCalendarById?calendarId=${calendarId}&page=${page}&size=${size}&sortBy=${sortBy}&sortDir=${sortDir}&searchKeyword=${searchKeyword}&searchField=${searchField}`)
      .pipe(map(res => {
        return res;
      }));
  }

  getTrackLeaveList(page: number, size: number, sortDir: string, sortBy: string, searchField: string, searchKeyword: string) {
    let params = new HttpParams()
      .set('empId', localStorage.getItem('empId') || '')
      .set('page', page)
      .set('size', size);

    sortBy && sortDir && (params = params.set('sortBy', sortBy).set('sortDir', sortDir));
    searchField?.trim() && searchKeyword?.trim() &&
      (params = params.set('searchField', searchField).set('searchKeyword', searchKeyword));

    return this.http.get<any>(`${environment.apiUrl}/api/leave-track/getAllTrackLeaves`, { params }).pipe(map(res => res));
  }

  getLeaveHistoryList(page: number, size: number, sortDir: string, sortBy: string) {
    let params = new HttpParams()
      .set('empId', localStorage.getItem('empId') || '')
      // .set('userCode', localStorage.getItem('userCode') || 'ANPL321')
      .set('page', page)
      .set('size', size);

    sortBy && sortDir && (params = params.set('sortBy', sortBy).set('sortDir', sortDir));

    return this.http.get<any>(`${environment.apiUrl}/api/leave/leave-historical-data`, { params });
  }


  jobOpeningDropdownList() {
    return this.http.get(`${environment.apiUrl}/api/jobOpening/jobOpeningDropdownList`)
      .pipe(map(res => {
        return res;
      }));
  }


  termsConditionDropdownList() {
    return this.http.get(`${environment.apiUrl}/api/job-terms/termsConditionDropdownList`)
      .pipe(map(res => {
        return res;
      }));
  }

  divisionList() {
    return this.http.get(`${environment.apiUrl}/api/user-division-master/findDivisionIdAndName`)
      .pipe(map(res => {
        return res;
      }));
  }


  separationList(page: number, size: number, sortDir: string, sortBy: string, searchField: string, searchKeyword: string) {
    return this.http.get<any>(`${environment.apiUrl}/api/track-separation/separation-list?page=${page}&size=${size}&sortBy=${sortBy}&sortDir=${sortDir}&searchField=${searchField}&searchKeyword=${searchKeyword}`)
      .pipe(map(res => {
        return res;
      }));
  }


  validateIfsc(): AsyncValidatorFn {
    return (control: AbstractControl) => {
      const ifsc = control.value;
      if (!ifsc || control.hasError('pattern')) {
        return of(null)
      }

      return this.http.get(`https://ifsc.razorpay.com/${ifsc}`, { responseType: 'json' })
        .pipe(
          map((res: any) => {
            return res && res.BANK ? null : { invalidIfsc: true };
          }),
          catchError(() => of({ invalidIfsc: true }))
        );
    };
  }


  getWorkflowDetailsByRequestNumber(requestNumber: any) {
    return this.http.get(`${environment.apiUrl}/api/workFlow/track/${requestNumber}`)
      .pipe(map(res => {
        return res;
      }));
  }

  getActiveShifts() {
    return this.http.get(`${environment.apiUrl}/api/shift-assignment/getActiveShifts`)
      .pipe(map(res => {
        return res;
      }));
  }


  getMessageList(page: number, size: number, sortDir: string, sortBy: string, searchField: string, searchKeyword: string, moduleId: number, functionId: number) {
    return this.http.get(`${environment.apiUrl}/api/message/get-all?page=${page}&size=${size}&sortBy=${sortBy}&sortDir=${sortDir}&searchField=${searchField}&searchKeyword=${searchKeyword}&moduleId=${moduleId}&functionId=${functionId}`)
      .pipe(map(res => {
        return res;
      }));
  }

  getMessageCodes() {
    return this.http.get(`${environment.apiUrl}/api/message/get-message-codes`)
      .pipe(map(res => {
        return res;
      }));
  }

  getMessageByFunctiondId(functionId: number) {
    return this.http.get(`${environment.apiUrl}/api/message/messages-by-functionId/${functionId}`)
      .pipe(map(res => {
        return res;
      }));
  }

  saveMessages(payload: any) {
    return this.http.post(`${environment.apiUrl}/api/message/save-messages`, payload)
      .pipe(map(res => {
        return res;
      }));
  }

  softwarePolicyList(employeeId: any, page: number, size: number, sortDir: string, sortBy: string, searchField: string, searchKeyword: string) {
    return this.http.get(`${environment.apiUrl}/api/policy/software-policy?employeeId=${employeeId}&page=${page}&size=${size}&sortBy=${sortBy}&sortDir=${sortDir}&searchField=${searchField}&searchKeyword=${searchKeyword}`)
      .pipe(map(res => {
        return res;
      }));
  }

  saveSequence(payload: any) {
    return this.http.post(`${environment.apiUrl}/api/sequence`, payload)
      .pipe(map(res => {
        return res;
      }));
  }

  savePolicy(payload: any) {
    return this.http.post(`${environment.apiUrl}/api/policy/save-policy`, payload)
      .pipe(map(res => {
        return res;
      }));
  }

  getSequence(sequenceId: any) {
    return this.http.get(`${environment.apiUrl}/api/sequence/${sequenceId}`)
      .pipe(map(res => {
        return res;
      }));
  }

  getPublicIP() {
    return this.http.get("https://api.ipify.org?format=json")
      .pipe(map(res => {
        return res;
      }));
  }

  sequenceList(page: number, size: number, sortBy: any, sortOrder: any, searchValue: any, searchColumn: any) {
    let params = new HttpParams()
      .set('page', page.toString())
      .set('size', size.toString())
      .set('sortBy', sortBy ?? '')
      .set('sortDir', sortOrder ?? '')
      .set('searchColumn', searchColumn ?? '')
      .set('searchValue', searchValue ?? '');
    return this.http.get<any>(`${environment.apiUrl}/api/sequence`, { params });
  }


  getSoftwarePolicyGroupList(page: number, size: number, sortDir: string, sortBy: string, searchField: string, searchKeyword: string) {
    return this.http.get<any>(`${environment.apiUrl}/api/policy/get-policy-group?page=${page}&size=${size}&sortBy=${sortBy}&sortDir=${sortDir}&searchField=${searchField}&searchKeyword=${searchKeyword}`)
      .pipe(map(res => {
        return res;
      }));
  }

  saveSoftwarePolicyGroup(payload: any) {
    return this.http.post(`${environment.apiUrl}/api/policy/save-policy-group`, payload)
      .pipe(map(res => {
        return res;
      }));
  }

  getSoftwarePolicyGroupById(id: any) {
    return this.http.get(`${environment.apiUrl}/api/policy/get-policy-group/${id}`)
      .pipe(map(res => {
        return res;
      }));
  }
  

  SalaryComponentList(page: number, size: number, sortDir: string, sortBy: string, searchField: string, searchKeyword: string) {
    return this.http.get<any>(`${environment.apiUrl}/api/salaryComponent/list?page=${page}&size=${size}&searchField=${searchField}&searchKeyword=${searchKeyword}&sortBy=${sortBy}&sortDir=${sortDir}`)
      .pipe(map(res => {
        return res;
      }));
  }
  SaveSalaryComponent(payload: any) {
    return this.http.post<any>(`${environment.apiUrl}/api/salaryComponent/save`, payload)
      .pipe(map(res => {
        return res;
      }
      ));
  }
  GetSalaryComponentById(id: any) {
    return this.http.get<any>(`${environment.apiUrl}/api/salaryComponent/${id}`)
      .pipe(map(res => {
        return res;
      }));
  }

  getCurrency() {
    return this.http.get(`${environment.apiUrl}/api/project/getCurrencyList`)
      .pipe(map(res => {
        return res;
      }));
  }
  striphtmlTags(html: string): string {
    const div = document.createElement('div');
    div.innerHTML = html;
    return div.textContent || div.innerText || '';
  }

  getVersion() {
    return this.http.get(`${environment.apiUrl}/api/build`)
      .pipe(map(res => {
        return res;
      }));
  }

  saveInternalJobApplication(payload: any) {
    return this.http.post<any>(`${environment.apiUrl}/api/jobOpening/internal-job`, payload)
      .pipe(map(res => {
        return res;
      }));
  }

  savePerformanceAppraisal(payload: any) {
    return this.http.post<any>(`${environment.apiUrl}/api/performance-assessment/save-mid-year-review`, payload)
      .pipe(map(res => {
        return res;
      }));
  }

  getPerformanceAppraisal(empId: number, period: string) {
    return this.http.get<any>(
      `${environment.apiUrl}/api/performance-assessment/${empId}/${period}`
    ).pipe(
      map(res => res),
      catchError(() => of({ data: null }))
    );
  }

  getPerformanceAssessmentById(id: any) {
    return this.http.get<any>(`${environment.apiUrl}/api/performance-assessment/${id}`)
      .pipe(map(res => {
        return res;
      }));
  }

  downloadPerformanceAttachment(performanceId: number, attachmentId: number) {
    return this.http.get(
      `${environment.apiUrl}/api/performance-assessment/${performanceId}/attachments/${attachmentId}/download`,
      { responseType: 'blob' }
    );
  }

  deletePerformanceAttachment(performanceId: number, attachmentId: number) {
    return this.http.delete<any>(
      `${environment.apiUrl}/api/performance-assessment/${performanceId}/attachments/${attachmentId}`
    );
  }

  getPerformanceAssessmentManagerList(empId: number, period: string) {
    return this.http.get<any>(
      `${environment.apiUrl}/api/performance-assessment/appraiser/${empId}/${period}`
    ).pipe(
      map(res => res),
      catchError(() => of([]))
    );
  }

  saveManagerReview(performanceId: number, payload: any) {
    return this.http.post<any>(
      `${environment.apiUrl}/api/performance-assessment/appraiser/${performanceId}`,
      payload
    ).pipe(map(res => res));
  }
}